/* EditGroupSchedule.jsx */
import { toast } from 'react-hot-toast';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  TextField,
  Switch,
  Popover,
  Checkbox,
  FormControlLabel,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import './EditGroupSchedule.css';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';

import { axiosInstance } from '../../../../utils/tokenUtils';
import ScheduleCreationSuccessModel from '../../CreateSessionModel/ScheduleCreationSuccessModel/ScheduleCreationSuccessModel';
import DropDownIcon from '../../../../assets/ScheduleIcon/dropdown.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

// Global success modal with modern React 18 API
// let rootInstance = null;

// const showGlobalSuccessModal = () => {
//   // Create container if it doesn't exist
//   let container = document.getElementById('success-modal-root');
//   if (!container) {
//     container = document.createElement('div');
//     container.id = 'success-modal-root';
//     document.body.appendChild(container);
//   }

//   // Create root if it doesn't exist
//   if (!rootInstance) {
//     rootInstance = createRoot(container);
//   }

//   // Render success modal
//   rootInstance.render(
//     <Modal open={true} onClose={() => {}} className="success-modal">
//       <Box className="success-modal-content">
//         <ScheduleCreationSuccessModel sessionType="Group" />
//       </Box>
//     </Modal>
//   );

//   // Hide after timeout
//   setTimeout(() => {
//     rootInstance.render(null);
//   }, 2000);
// };

const LevelMap = {
  /* academic */
  Elementary: 'elementary',
  'Middle School': 'middle_school',
  'High School': 'high_school',
  University: 'university',
  /* creative */
  Beginner: 'beginner',
  Intermediate: 'intermediate',
  Professional: 'professional',
};
const ReverseLevelMap = Object.fromEntries(Object.entries(LevelMap).map(([k, v]) => [v, k]));

// Convert UTC time to local time and format to AM/PM
const formatTimeToAmPm = utcTime => {
  if (!utcTime) return '';

  // Convert UTC time string to dayjs object and then to local time
  const utcDate = dayjs.utc(`2023-01-01T${utcTime}`);
  return utcDate.local().format('hh:mm A');
};

// Convert 12h AM/PM format to 24h UTC format
const to24hUTC = localTime12h => {
  if (!localTime12h) return '';

  // Parse the local time using dayjs
  let localDate;
  if (localTime12h.includes('AM') || localTime12h.includes('PM')) {
    localDate = dayjs(`2023-01-01 ${localTime12h}`, 'YYYY-MM-DD hh:mm A');
  } else {
    const [hours, minutes] = localTime12h.split(':');
    localDate = dayjs().hour(hours).minute(minutes);
  }

  // Convert to UTC
  return localDate.utc().format('HH:mm');
};

/* -------------------------------------------------------------------------- */
/* ⇢ CLOCK VIEW RENDERER                                                      */
/* -------------------------------------------------------------------------- */
const customClock = props => renderTimeViewClock(props);

/* -------------------------------------------------------------------------- */
/* ⇢ COMPONENT                                                                */
/* -------------------------------------------------------------------------- */
export default function EditGroupSchedule({ open, onClose, availabilityId }) {
  /* ------------------------------ STATE ----------------------------------- */
  const blankDays = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };
  const blankSlots = Object.fromEntries(
    Object.keys(blankDays).map(d => [d, [{ from: '', to: '' }]])
  );

  const [subjects, setSubjects] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  const [subjectId, setSubjectId] = useState('');
  const [subject, setSubject] = useState('');
  const [tags, setTags] = useState([]);
  const [level, setLevel] = useState('');
  const [description, setDescription] = useState('');

  const [days, setDays] = useState(blankDays);
  const [timeSlots, setTimeSlots] = useState(blankSlots);

  const [teacherType, setTeacherType] = useState('academic');
  const [teacherData, setTeacherData] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);

  const [errorMessage, setError] = useState('');
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  // Removed showSuccess state since we're using global modal

  /* copy-time-slot popover */
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const [copySourceDay, setCopySourceDay] = useState('');
  const [copySourceIndex, setCopySourceIndex] = useState(null);
  const [copyTargetDays, setCopyTargetDays] = useState(blankDays);
  const copyButtonRefs = useRef({});

  /* time-picker portal */
  const [activePicker, setActivePicker] = useState(null); /* {day,idx,field} */
  const [clockView, setClockView] = useState('hours');
  const timeRefs = useRef({});

  /* modal container ref for stable anchoring */
  const modalRef = useRef(null);

  /* ------------------------------------------------------------------------ */
  /* ⇢ API CALLS                                                              */
  /* ------------------------------------------------------------------------ */
  const fetchTeacherData = async () => {
    const res = await axiosInstance.get('/teachers');
    const data = res.data?.data || res.data;
    setTeacherData(data);
    setTeacherType((data.teacher_type || data.type || 'academic').toLowerCase());
    setSubjects(data.subjects || []);
  };

  const fetchAvailabilityData = async () => {
    const res = await axiosInstance.get(`/teachers/availability/${availabilityId}`);
    const data = res.data?.data || res.data;
    setAvailabilityData(data);
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ MAIN EFFECT: on modal open, pull both resources (teacher ➜ availability) */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!open || !availabilityId) return;

    const getEverything = async () => {
      setIsLoading(true);
      setApiError('');
      try {
        await fetchTeacherData(); /* must finish first */
        await fetchAvailabilityData(); /* then we can parse it */
      } catch (err) {
        console.error(err);
        setApiError('Unable to load session data. Please refresh and try again.');
      } finally {
        setIsLoading(false);
      }
    };

    getEverything();
  }, [open, availabilityId]);

  /* ------------------------------------------------------------------------ */
  /* ⇢ When BOTH teacherData & availabilityData exist, map into form state     */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!teacherData || !availabilityData || dataFetched) return;
    processAvailabilityData(availabilityData);
  }, [teacherData, availabilityData]);

  /* ------------------------------------------------------------------------ */
  /* ⇢ UTIL: populate state from availabilityData                              */
  /* ------------------------------------------------------------------------ */
  const processAvailabilityData = data => {
    try {
      setDescription(data.description || '');
      setLevel(data.level_of_teaching || '');

      /* match subject */
      const subj =
        (teacherData.subjects || []).find(
          s => s.title.toLowerCase() === data.subject.toLowerCase()
        ) || {};
      setSubjectId(subj.id || '');
      setSubject(subj.title || data.subject || '');
      setAvailableTags(subj.tags || []);

      /* pre-select tags */
      const matchedTags = (data.tags || [])
        .map(name => (subj.tags || []).find(t => t.title.toLowerCase() === name.toLowerCase()))
        .filter(Boolean);
      setTags(matchedTags);

      /* days & slots */
      const d = { ...blankDays };
      const ts = { ...blankSlots };
      (data.availability || []).forEach(slot => {
        slot.days.forEach(cap => {
          const day = cap.toLowerCase();
          d[day] = true;
          ts[day].push({
            from: formatTimeToAmPm(slot.startTime),
            to: formatTimeToAmPm(slot.endTime),
          });
        });
      });
      Object.keys(ts).forEach(day => {
        if (ts[day].length > 1 && !ts[day][0].from && !ts[day][0].to) ts[day].shift();
      });
      setDays(d);
      setTimeSlots(ts);

      setDataFetched(true);
    } catch (e) {
      console.error(e);
      setApiError('Error parsing session data.');
    }
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ SUBJECT CHANGE => reset tags & availTag list                            */
  /* ------------------------------------------------------------------------ */
  const handleSubjectChange = (_, val) => {
    if (!val) {
      setSubject('');
      setSubjectId('');
      setAvailableTags([]);
      setTags([]);
      return;
    }
    setSubject(val.title);
    setSubjectId(val.id);
    setAvailableTags(val.tags || []);
    setTags([]);
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ DAYS & SLOTS helpers                                                    */
  /* ------------------------------------------------------------------------ */
  const toggleDay = d => {
    setDays(prev => ({ ...prev, [d]: !prev[d] }));
    setError('');
  };
  const addSlot = d => setTimeSlots(p => ({ ...p, [d]: [...p[d], { from: '', to: '' }] }));
  const removeSlot = (d, i) =>
    setTimeSlots(p => {
      const arr = p[d].filter((_, idx) => idx !== i);
      return { ...p, [d]: arr.length ? arr : [{ from: '', to: '' }] };
    });

  /* ------------------------------------------------------------------------ */
  /* ⇢ TIME PICKER                                                            */
  /* ------------------------------------------------------------------------ */
  const openPicker = (day, idx, field) => {
    setActivePicker({ day, idx, field });
    setClockView('hours');
  };
  const closePicker = () => setActivePicker(null);
  const onTimeChange = v => {
    if (!activePicker) return;
    const { day, idx, field } = activePicker;
    const formattedTime = v ? dayjs(v).format('hh:mm A') : '';
    const autoToTime =
      field === 'from' && formattedTime ? dayjs(v).add(1, 'hour').format('hh:mm A') : null;

    setTimeSlots(prev => {
      const slot = prev[day][idx];
      const updatedSlot = {
        ...slot,
        [field]: formattedTime,
        ...(field === 'from' && !slot.to && autoToTime ? { to: autoToTime } : {}),
      };
      return {
        ...prev,
        [day]: prev[day].map((s, i) => (i === idx ? updatedSlot : s)),
      };
    });
    if (clockView === 'hours') setClockView('minutes');
    else closePicker();
  };
  const renderPickerPortal = () => {
    if (!activePicker) return null;
    if (!modalRef.current) return null; /* ensure stable anchor */
    const { day, idx, field } = activePicker;
    return ReactDOM.createPortal(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          open
          ampm
          view={clockView}
          views={['hours', 'minutes']}
          minutesStep={15}
          value={dayjs(timeSlots[day][idx][field] || '12:00 AM', 'hh:mm A')}
          onViewChange={v => setClockView(v)}
          onClose={closePicker}
          onChange={onTimeChange}
          viewRenderers={{ hours: customClock, minutes: customClock }}
          slots={{ textField: () => null }}
          slotProps={{
            popper: {
              anchorEl: modalRef.current,
              placement: 'right',
              modifiers: [{ name: 'offset', options: { offset: [8, 0] } }],
              className: 'egs-timepicker-popper',
              disablePortal: false,
            },
          }}
        />
      </LocalizationProvider>,
      document.body
    );
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ COPY POPOVER helpers                                                    */
  /* ------------------------------------------------------------------------ */
  const openCopy = (e, d, i) => {
    e.stopPropagation();
    setCopyAnchorEl(copyButtonRefs.current[`${d}-${i}`] || e.currentTarget);
    setCopySourceDay(d);
    setCopySourceIndex(i);
    setCopyTargetDays({ ...blankDays });
    setError('');
  };
  const copySubmit = () => {
    if (!copySourceDay && copySourceIndex == null) return;
    const src = timeSlots[copySourceDay][copySourceIndex];

    if (!src?.from || !src?.to) {
      setError('Please select both "From" and "To" times for the source slot before copying.');
      return;
    }

    const selectedTargets = Object.keys(copyTargetDays).filter(
      d => d !== copySourceDay && copyTargetDays[d]
    );

    if (selectedTargets.length === 0) {
      setError('Please select at least one day to copy to.');
      return;
    }

    setTimeSlots(prev => {
      const obj = { ...prev };

      selectedTargets.forEach(d => {
        if (!Array.isArray(obj[d])) obj[d] = [];

        if (obj[d].length === 0) {
          obj[d] = [{ from: src.from, to: src.to }];
        } else {
          const hasOnlyEmptyDefault = obj[d].length === 1 && !obj[d][0]?.from && !obj[d][0]?.to;

          if (hasOnlyEmptyDefault) {
            obj[d][0] = { from: src.from, to: src.to };
          } else {
            const isDuplicate = obj[d].some(s => s.from === src.from && s.to === src.to);
            if (!isDuplicate) obj[d].push({ from: src.from, to: src.to });
          }
        }

        setDays(p => ({ ...p, [d]: true }));
      });

      return obj;
    });

    setError('');
    setCopyAnchorEl(null);
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ VALIDATE & SAVE                                                         */
  /* ------------------------------------------------------------------------ */
  const validate = () => {
    setError('');
    if (!subjectId) return (setError('Please select a subject'), false);
    if (!level) return (setError('Please select a level'), false);
    if (!Object.values(days).some(Boolean))
      return (setError('Please select at least one day'), false);
    const bad = Object.keys(days).find(d => days[d] && timeSlots[d].some(s => !s.from || !s.to));
    if (bad) return (setError(`Fill all time fields for ${bad}`), false);
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const apiTimeSlots = {};
    Object.keys(days).forEach(d => {
      if (!days[d]) return;
      // Capitalize first letter of day name
      const capitalizedDay = d.charAt(0).toUpperCase() + d.slice(1);
      apiTimeSlots[capitalizedDay] = timeSlots[d].map(s => ({
        start: to24hUTC(s.from),
        end: to24hUTC(s.to),
      }));
    });

    const payload = {
      event_type_id: availabilityData.id,
      subject: subjectId,
      tags: tags.map(t => t.id),
      levels_to_teach: level,
      description,
      time_slots: apiTimeSlots,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      await axiosInstance.patch('/teachers/availability', payload);

      // Quietly refresh calendar data if function exists
      if (window.refreshCalendarData) {
        window.refreshCalendarData();
      }

      // First close the edit modal
      onClose();
      // Then show toast notification
      toast.success('Schedule updated successfully');
    } catch (err) {
      console.error(err);
      let friendly = 'Failed to update schedule. Please try again.';
      const data = err?.response?.data;
      if (data?.detail) {
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          friendly = data.detail[0]?.msg || data.detail[0]?.message || String(data.detail[0]);
        } else if (typeof data.detail === 'string') {
          friendly = data.detail;
        } else if (typeof data.detail === 'object') {
          friendly = data.detail.message || JSON.stringify(data.detail);
        }
      } else if (data?.message) {
        friendly = data.message;
      } else if (err.message) {
        friendly = err.message;
      }
      setApiError(friendly);
      toast.error(friendly);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ RESET on close                                                         */
  /* ------------------------------------------------------------------------ */
  const resetAll = () => {
    setSubject('');
    setSubjectId('');
    setAvailableTags([]);
    setTags([]);
    setLevel('');
    setDescription('');
    setDays(blankDays);
    setTimeSlots(blankSlots);
    setError('');
    setApiError('');
    setDataFetched(false);
    setAvailabilityData(null);
    setTeacherData(null);
  };
  const handleClose = () => {
    resetAll();
    onClose();
  };

  /* ------------------------------------------------------------------------ */
  /* ⇢ RENDERERS                                                              */
  /* ------------------------------------------------------------------------ */
  const Loading = () => (
    <Box className="edit-group-loading-container">
      <CircularProgress />
      <Typography className="edit-group-loading-text">Loading session…</Typography>
    </Box>
  );

  const DaySlots = ({ day }) => (
    <Box className="edit-group-time-slot-container">
      {timeSlots[day].map((slot, idx) => (
        <Box key={idx} className="edit-group-time-slot">
          <Box className="edit-group-time-inputs">
            {['from', 'to'].map(field => (
              <Box key={field} className="edit-group-time-input-group">
                <Typography className="edit-group-time-label">
                  {field === 'from' ? 'From' : 'To'}
                </Typography>
                <div
                  className="edit-group-time-input-wrapper"
                  onClick={() => openPicker(day, idx, field)}
                  ref={el => (timeRefs.current[`${day}-${idx}-${field}`] = el)}
                >
                  <div className="edit-group-time-input-icon">
                    <AccessTimeIcon fontSize="small" />
                  </div>
                  <input
                    className="edit-group-time-input-field"
                    value={slot[field] || ''}
                    placeholder="Select time"
                    readOnly
                  />
                </div>
              </Box>
            ))}
            {/* actions */}
            <Box className="edit-group-time-actions">
              <IconButton onClick={() => addSlot(day)} className="edit-group-time-action-button">
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={e => openCopy(e, day, idx)}
                className="edit-group-time-action-button"
                ref={el => (copyButtonRefs.current[`${day}-${idx}`] = el)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              {timeSlots[day].length > 1 && (
                <IconButton
                  onClick={() => removeSlot(day, idx)}
                  className="edit-group-time-action-button"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  /* ------------------------------------------------------------------------ */
  /* ⇢ JSX                                                                    */
  /* ------------------------------------------------------------------------ */
  return (
    <>
      <Modal open={open} onClose={handleClose} className="edit-group-schedule-modal">
        <Box className="edit-group-schedule-modal-content" ref={modalRef}>
          {/* Header */}
          <Box className="edit-group-schedule-modal-header">
            <Typography className="edit-group-schedule-modal-title">Edit Group Session</Typography>
            <IconButton onClick={handleClose} className="edit-group-close-button">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body */}
          <Box className="edit-group-schedule-modal-body">
            {isLoading && !dataFetched ? (
              <Loading />
            ) : (
              <>
                {errorMessage && (
                  <Alert severity="error" className="edit-group-form-error">
                    {errorMessage}
                  </Alert>
                )}

                {/* Subject */}
                <Box className="edit-group-select-container">
                  <Typography className="edit-group-select-label">Subject</Typography>
                  <Autocomplete
                    options={subjects}
                    value={subjects.find(s => s.id === subjectId) || null}
                    onChange={handleSubjectChange}
                    getOptionLabel={o => o.title}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={p => <TextField {...p} placeholder="Select a subject" fullWidth />}
                    className="edit-group-subject-select"
                  />
                </Box>

                {/* Tags */}
                <Box className="edit-group-select-container">
                  <Typography className="edit-group-select-label">Tags (optional)</Typography>
                  <Autocomplete
                    multiple
                    options={availableTags}
                    value={tags}
                    onChange={(e, v) => setTags(v)}
                    getOptionLabel={o => o.title}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    disableCloseOnSelect
                    renderTags={() => null}
                    renderInput={p => (
                      <TextField
                        {...p}
                        placeholder={availableTags.length ? 'Type here…' : 'Select a subject first'}
                        fullWidth
                      />
                    )}
                    className="edit-group-tags-autocomplete"
                  />
                  {tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {tags.map(tag => (
                        <Chip
                          key={tag.id}
                          label={tag.title}
                          onDelete={() => setTags(tags.filter(t => t.id !== tag.id))}
                          className="edit-group-tag-chip"
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Level */}
                <Box className="edit-group-select-container">
                  <Typography className="edit-group-select-label">Level to Teach</Typography>
                  <FormControl fullWidth variant="outlined" className="edit-group-select-field">
                    <Select
                      value={level}
                      onChange={e => setLevel(e.target.value)}
                      displayEmpty
                      renderValue={v => (v ? ReverseLevelMap[v] : 'Select level')}
                      IconComponent={() => (
                        <Box className="edit-group-dropdown-icon">
                          <img src={DropDownIcon} alt="dropdown" />
                        </Box>
                      )}
                    >
                      <MenuItem disabled value="">
                        Select level
                      </MenuItem>
                      {teacherData &&
                      teacherData.level_of_teaching &&
                      teacherData.level_of_teaching.length > 0
                        ? teacherData.level_of_teaching.map(level => (
                            <MenuItem key={level} value={level}>
                              {ReverseLevelMap[level] ||
                                level.charAt(0).toUpperCase() + level.slice(1)}
                            </MenuItem>
                          ))
                        : Object.entries(LevelMap).map(([disp, api]) => (
                            <MenuItem key={api} value={api}>
                              {disp}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Days & Slots */}
                <Box className="edit-group-days-container">
                  {Object.keys(days).map(d => (
                    <Box key={d} className="edit-group-day-section">
                      <Box className="edit-group-day-toggle">
                        <Typography className="edit-group-day-name">
                          {d.charAt(0).toUpperCase() + d.slice(1)}
                        </Typography>
                        <Switch
                          checked={days[d]}
                          onChange={() => toggleDay(d)}
                          className="edit-group-day-switch"
                        />
                      </Box>
                      {days[d] && <DaySlots day={d} />}
                    </Box>
                  ))}
                </Box>

                {/* Description */}
                <Box className="edit-group-description-container">
                  <Typography className="edit-group-description-label">
                    Write a short description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter here…"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    variant="outlined"
                    className="edit-group-description-field"
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Footer */}
          <Box className="edit-group-schedule-modal-footer">
            <Button onClick={handleClose} variant="outlined" className="edit-group-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isLoading}
              className="edit-group-save-btn"
            >
              {isLoading ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Time-picker portal */}
      {renderPickerPortal()}

      {/* Copy popover */}
      <Popover
        open={Boolean(copyAnchorEl)}
        anchorEl={modalRef.current}
        onClose={() => setCopyAnchorEl(null)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        className="egs-copy-popover"
      >
        <Box className="edit-group-copy-popover-fixed-content">
          <Typography className="edit-group-copy-title">Copy times to</Typography>
          <Box className="edit-group-copy-days">
            {Object.keys(copyTargetDays)
              .filter(d => d !== copySourceDay)
              .map(d => (
                <FormControlLabel
                  key={d}
                  control={
                    <Checkbox
                      checked={copyTargetDays[d]}
                      onChange={() => setCopyTargetDays(p => ({ ...p, [d]: !p[d] }))}
                    />
                  }
                  label={d.charAt(0).toUpperCase() + d.slice(1)}
                  className="edit-group-copy-day-checkbox"
                />
              ))}
          </Box>
          <Button
            variant="contained"
            disabled={!Object.values(copyTargetDays).some(Boolean)}
            onClick={copySubmit}
            className="edit-group-copy-submit-btn"
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Success modal */}
      {/* This modal is now rendered globally */}
    </>
  );
}
